import { useMemo, useState } from 'react';
import {
  Anchor, Badge, Button, Code, Group, Select, Table, Text, UnstyledButton,
} from '@mantine/core';
import { Link } from 'react-router-dom';

import { useEventNames, useEvents, useOverview } from '../api/hooks';
import { DataState } from '../components/DataState';
import { PageHeader } from '../components/PageHeader';
import { fmtDate, fmtNumber, fmtRelative } from '../theme/format';

export function EventsPage() {
  const [name, setName] = useState<string | null>(null);
  const { data: names } = useEventNames();
  const { data: overview } = useOverview(7);
  const {
    data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useEvents(name ?? undefined);

  const events = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );

  const counts = overview?.events ?? [];

  return (
    <>
      <PageHeader
        title="Events"
        description="Everything the SDKs have tracked via POST /v1/events — the same stream that feeds segments and journey triggers."
      />

      {counts.length > 0 && (
        <Group gap="xs" mb="md">
          <Text size="xs" c="dimmed" mr={4}>Last 7 days</Text>
          {counts.map((c) => {
            const active = name === c.name;
            return (
              <UnstyledButton
                key={c.name}
                onClick={() => setName(active ? null : c.name)}
              >
                <Badge
                  size="lg"
                  variant={active ? 'filled' : 'light'}
                  color={active ? 'blue' : 'gray'}
                  rightSection={
                    <Text size="xs" fw={600} ml={4}>{fmtNumber(c.total)}</Text>
                  }
                >
                  {c.name}
                </Badge>
              </UnstyledButton>
            );
          })}
        </Group>
      )}

      <Group mb="md">
        <Select
          size="xs"
          w={280}
          clearable
          searchable
          placeholder="Filter by event name"
          data={names ?? []}
          value={name}
          onChange={setName}
        />
        {events.length > 0 && (
          <Text size="xs" c="dimmed">
            Showing {fmtNumber(events.length)}
            {hasNextPage ? '+' : ''} events
          </Text>
        )}
      </Group>

      <DataState
        loading={isLoading}
        error={error}
        empty={!events.length}
        emptyTitle="No events yet"
        emptyDescription="Call POST /v1/events with a public key from your app. Events for unknown profiles are kept with a null profile so nothing is lost."
      >
        <Table.ScrollContainer minWidth={860}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>When</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Profile</Table.Th>
                <Table.Th>Properties</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {events.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Text size="xs" c="dimmed" title={fmtDate(e.timestamp)} style={{ whiteSpace: 'nowrap' }}>
                      {fmtRelative(e.timestamp)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace" fw={500}>{e.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    {e.profile_id ? (
                      <Anchor component={Link} to={`/profiles/${e.profile_id}`} size="sm">
                        {e.profile_id.slice(0, 8)}
                      </Anchor>
                    ) : (
                      <Text size="sm" c="dimmed">unidentified</Text>
                    )}
                  </Table.Td>
                  <Table.Td maw={420}>
                    {Object.keys(e.props ?? {}).length > 0 ? (
                      <Code style={{ fontSize: 11 }}>
                        {JSON.stringify(e.props).slice(0, 120)}
                      </Code>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {hasNextPage && (
          <Group justify="center" mt="md">
            <Button
              variant="default"
              size="xs"
              loading={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              Load more
            </Button>
          </Group>
        )}
      </DataState>
    </>
  );
}
